import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { cn } from '../utils';

interface MarketInfo {
  name: string;
  openTime: string; // HH:MM format in GMT
  closeTime: string; // HH:MM format in GMT
  timezone: string;
}

interface MarketStatusInfo {
  market: string;
  status: 'Open' | 'Closed' | 'Pre-Market' | 'After-Hours';
  color: string;
  currentTime: string;
  nextEvent: string;
}

const MARKETS: MarketInfo[] = [
  {
    name: 'London Stock Exchange',
    openTime: '08:00',
    closeTime: '16:30',
    timezone: 'GMT'
  },
  {
    name: 'New York (NYSE/NASDAQ)',
    openTime: '13:30',
    closeTime: '20:00',
    timezone: 'GMT'
  },
  {
    name: 'Tokyo Stock Exchange',
    openTime: '00:00',
    closeTime: '09:00',
    timezone: 'GMT'
  }
];

const parseTime = (timeStr: string): { hours: number; minutes: number } => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return { hours, minutes };
};

const getMinutesFromMidnight = (hours: number, minutes: number): number => {
  return hours * 60 + minutes;
};

const formatTime = (hours: number, minutes: number): string => {
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const getMarketStatus = (market: MarketInfo, currentGMT: Date): MarketStatusInfo => {
  const currentHours = currentGMT.getUTCHours();
  const currentMinutes = currentGMT.getUTCMinutes();
  const currentTimeInMinutes = getMinutesFromMidnight(currentHours, currentMinutes);
  
  const openTime = parseTime(market.openTime);
  const closeTime = parseTime(market.closeTime);
  const openTimeInMinutes = getMinutesFromMidnight(openTime.hours, openTime.minutes);
  const closeTimeInMinutes = getMinutesFromMidnight(closeTime.hours, closeTime.minutes);
  
  let status: 'Open' | 'Closed' | 'Pre-Market' | 'After-Hours';
  let color: string;
  let nextEvent: string;
  
  // Handle markets that cross midnight (like Tokyo)
  if (openTimeInMinutes > closeTimeInMinutes) {
    // Market crosses midnight
    if (currentTimeInMinutes >= openTimeInMinutes || currentTimeInMinutes <= closeTimeInMinutes) {
      status = 'Open';
      color = 'text-green-400';
      if (currentTimeInMinutes >= openTimeInMinutes) {
        // Before midnight
        const minutesToClose = (24 * 60) - currentTimeInMinutes + closeTimeInMinutes;
        const hoursToClose = Math.floor(minutesToClose / 60);
        const minsToClose = minutesToClose % 60;
        nextEvent = `Closes in ${hoursToClose}h ${minsToClose}m`;
      } else {
        // After midnight
        const minutesToClose = closeTimeInMinutes - currentTimeInMinutes;
        const hoursToClose = Math.floor(minutesToClose / 60);
        const minsToClose = minutesToClose % 60;
        nextEvent = `Closes in ${hoursToClose}h ${minsToClose}m`;
      }
    } else {
      status = 'Closed';
      color = 'text-red-400';
      const minutesToOpen = openTimeInMinutes - currentTimeInMinutes;
      const hoursToOpen = Math.floor(minutesToOpen / 60);
      const minsToOpen = minutesToOpen % 60;
      nextEvent = `Opens in ${hoursToOpen}h ${minsToOpen}m`;
    }
  } else {
    // Normal market hours (same day)
    if (currentTimeInMinutes >= openTimeInMinutes && currentTimeInMinutes <= closeTimeInMinutes) {
      status = 'Open';
      color = 'text-green-400';
      const minutesToClose = closeTimeInMinutes - currentTimeInMinutes;
      const hoursToClose = Math.floor(minutesToClose / 60);
      const minsToClose = minutesToClose % 60;
      nextEvent = `Closes in ${hoursToClose}h ${minsToClose}m`;
    } else if (currentTimeInMinutes < openTimeInMinutes) {
      // Before market opens
      const minutesToOpen = openTimeInMinutes - currentTimeInMinutes;
      if (minutesToOpen <= 60) {
        status = 'Pre-Market';
        color = 'text-yellow-400';
      } else {
        status = 'Closed';
        color = 'text-red-400';
      }
      const hoursToOpen = Math.floor(minutesToOpen / 60);
      const minsToOpen = minutesToOpen % 60;
      nextEvent = `Opens in ${hoursToOpen}h ${minsToOpen}m`;
    } else {
      // After market closes
      const minutesToNextOpen = (24 * 60) - currentTimeInMinutes + openTimeInMinutes;
      if (currentTimeInMinutes - closeTimeInMinutes <= 60) {
        status = 'After-Hours';
        color = 'text-orange-400';
      } else {
        status = 'Closed';
        color = 'text-red-400';
      }
      const hoursToOpen = Math.floor(minutesToNextOpen / 60);
      const minsToOpen = minutesToNextOpen % 60;
      nextEvent = `Opens in ${hoursToOpen}h ${minsToOpen}m`;
    }
  }
  
  const currentTime = formatTime(currentHours, currentMinutes);
  
  return {
    market: market.name,
    status,
    color,
    currentTime,
    nextEvent
  };
};

export const MarketStatus: React.FC = () => {
  const [marketStatuses, setMarketStatuses] = useState<MarketStatusInfo[]>([]);
  
  const updateMarketStatuses = () => {
    const now = new Date();
    
    const statuses = MARKETS.map(market => getMarketStatus(market, now));
    setMarketStatuses(statuses);
  };
  
  useEffect(() => {
    // Update immediately
    updateMarketStatuses();
    
    // Update every minute
    const interval = setInterval(updateMarketStatuses, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="space-y-4">
      {/* Local System Time */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/20">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-green-400" />
          <span className="text-sm font-medium text-green-300">Local System Time</span>
        </div>
        <span className="text-sm font-mono text-green-400">{new Date().toLocaleTimeString()}</span>
      </div>
      
      {/* Market Statuses */}
      <div className="space-y-3">
        {marketStatuses.map((market, index) => (
          <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
            <div className="flex flex-col">
              <span className="text-gray-300 text-sm font-medium">{market.market}</span>
              <span className="text-xs text-gray-400">{market.nextEvent}</span>
            </div>
            <div className="text-right">
              <span className={cn("text-sm font-medium", market.color)}>
                {market.status}
              </span>
              <div className="text-xs text-gray-400 mt-1">
                {market.market === 'London Stock Exchange' && '08:00-16:30 GMT'}
                {market.market === 'New York (NYSE/NASDAQ)' && '13:30-20:00 GMT'}
                {market.market === 'Tokyo Stock Exchange' && '00:00-09:00 GMT'}
              </div>
            </div>
          </div>
        ))}
        
        {/* Crypto Markets - Always Open */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
          <div className="flex flex-col">
            <span className="text-gray-300 text-sm font-medium">Crypto Markets</span>
            <span className="text-xs text-gray-400">Always available</span>
          </div>
          <div className="text-right">
            <span className="text-sm font-medium text-blue-400">24/7 Open</span>
            <div className="text-xs text-gray-400 mt-1">Continuous</div>
          </div>
        </div>
      </div>
    </div>
  );
};